import { DateFormats, formatDate } from '@strata/helper-functions';
import { SingleSelectWithOther } from '@strata/react-compound-inputs';
import { DateInputFormik } from '@strata/react-date-time';
import { DialogWithForm } from '@strata/react-dialog';
import { TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { handleError } from 'services/Error';
import {
    DocumentableFillActivitiesMapReverse,
    DocumentableFillActivityOptions,
    SHIPPED_ACTIVITY,
} from 'services/fillActivityHelpers';
import { usePostShippingInfoMutation } from 'store/api/onboardingApi';
import { usePostFillActivityMutation } from 'store/api/userSupportApi';

const CarrierOptions = ['DHL', 'FedEx', 'UPS', 'USPS'];

interface LogFillActivityDialogProps {
    isOpen?: boolean;
    referralDetails?: {
        referralId: string;
        payerId: string;
    };
    onClose: () => void;
}

export const LogFillActivityDialog = ({
    isOpen = false,
    referralDetails = undefined,
    onClose,
}: LogFillActivityDialogProps) => {
    const referralId = referralDetails?.referralId;
    const payerId = referralDetails?.payerId;
    const [postFillActivity] = usePostFillActivityMutation();
    const [postShippingInformation] = usePostShippingInfoMutation();

    const validationSchema = Yup.object({
        status: Yup.object({
            name: Yup.string(),
            quickFilterKey: Yup.string(),
        }).required(validationStrings.requiredField.errorMessage),
        tracking_number: Yup.string(),
        shipment_date: DateInputFormik.validator(),
        ...SingleSelectWithOther.validationSchema({
            id: 'shipment_carrier',
            required: false,
        }),
        shipment_carrier: Yup.string(),
    });

    return (
        <Formik
            initialValues={{} as Yup.InferType<typeof validationSchema>}
            onSubmit={async (values, { resetForm }) => {
                const selectedStatus = values.status;
                const statusKey = DocumentableFillActivitiesMapReverse[selectedStatus.name!];
                try {
                    await postFillActivity({
                        referralId: referralId!,
                        body: {
                            status: statusKey,
                        },
                    }).unwrap();
                    if (statusKey === SHIPPED_ACTIVITY && values.tracking_number) {
                        await postShippingInformation({
                            referralId: referralId!,
                            payerId: payerId!,
                            body: {
                                tracking_number: values.tracking_number,
                                shipment_date: values.shipment_date
                                    ? formatDate(values.shipment_date, DateFormats.HYPHEN_DATE)
                                    : null,
                                ...(values.shipment_carrier
                                    ? SingleSelectWithOther.submitValues({
                                          id: 'shipment_carrier',
                                      })(values)
                                    : {
                                          shipment_carrier: null,
                                      }),
                            },
                        }).unwrap();
                    }
                    SuccessToast({ message: 'Fill activity documented successfully.' });
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError(
                        'Unable to document fill activity. Please try again.',
                        err as Error,
                    );
                }
            }}
            validationSchema={validationSchema}
            enableReinitialize
        >
            {(formik) => {
                const selectedStatus = formik.values.status;
                const statusKey = selectedStatus
                    ? DocumentableFillActivitiesMapReverse[selectedStatus.name!]
                    : undefined;
                return (
                    <DialogWithForm
                        isOpen={isOpen}
                        dialogTitle="Log Fill Activity"
                        isPrimaryButtonDisabled={
                            !(formik.dirty && formik.isValid) || formik.isSubmitting
                        }
                        onSecondaryButtonClick={() => {
                            onClose();
                            formik.resetForm();
                        }}
                        primaryButtonLabel="Log Fill Activity"
                        secondaryButtonLabel="Cancel"
                        dialogContent={
                            <>
                                <SuggestSingleSelectFormik
                                    id="status"
                                    label="Fill Activity"
                                    required
                                    values={DocumentableFillActivityOptions}
                                    displaySecondaryDataBelowOption
                                />
                                {statusKey === SHIPPED_ACTIVITY && (
                                    <>
                                        <TextInputFormik
                                            id="tracking_number"
                                            label="Last Tracking Number"
                                        />
                                        {formik.values.tracking_number && (
                                            <>
                                                <DateInputFormik
                                                    id="shipment_date"
                                                    label="Shipment Date"
                                                />
                                                <SingleSelectWithOther
                                                    id="shipment_carrier"
                                                    label="Shipment Carrier"
                                                    values={CarrierOptions}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        }
                    />
                );
            }}
        </Formik>
    );
};
